import useSettings from '../../../hooks/useSettings';
import Iconify from '../../../components/Iconify';
import Page from '../../../components/Page';
import { Box, Container, Tab, Tabs, TextField, Typography } from '@mui/material';
import { capitalCase } from 'change-case';
import DeliveryType from './components/deliveryType';
import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { CustomerMobileNumber } from './components/customerMobileNumber';
import { OrderInformation } from './components/orderInformation';
import { dispatch, useSelector } from '../../../redux/store';
import { getAllSaleTypes } from '../../../redux/slices/saleRedux/salesRedux';
import { getFoodCategorys } from '../../../redux/slices/stockManagementRedux/foodCategoryRedux';
import { getMenuAndVariationFoods } from '../../../redux/slices/stockManagementRedux/foodRedux';
import { getPaymentTypes } from '../../../redux/slices/financeRedux/paymentTypeRedux';
import { Payments } from './components/payments';
import { LoadingButton } from '@mui/lab';
import { hidePOSTab, openPOSDrawer, updateUserDataAfterUpdate } from '../../../redux/slices/posRedux/posRedux';
import { get, post } from '../../../inteceptor';
import { toast } from 'react-toastify';
import { BASE_URL, ENV } from '../../../config';
import { getPosAccessState, getPosSessionIdentifierLabel } from './posSessionLifecycle';

export default function POS({ isDrawer = false }) {
  const { themeStretch } = useSettings();
  const [currentTab, setCurrentTab] = useState('delivery_type');
  const { viewPosStatus, drawerOpeningStatus, sessionContext, sessionContextResolved } = useSelector((state) => state?.pos);
  const { loggedInUser } = useSelector((state) => state?.user);
  const [startingAmount, setStartingAmount] = useState(15000);
  const { selectedLocation } = useSelector((state) => state?.location);
  const posAccess = getPosAccessState({ sessionContext, sessionContextResolved, loggedInUser, selectedLocation });
  const sessionIdentifiers = getPosSessionIdentifierLabel(posAccess);

  const posDataDefault = {
    deliveryInfo: {},
    posCashDrawersId: posAccess.drawerId,
    locationInfo: selectedLocation,
    customerInfo: {
      id: 0,
      name: '',
      phone: '94',
      birthdayMonth: 0,
      birthdayDay: 0,
      sendSMS: true,
    },
    orderInfo: {
      cart: [],
      specialNote: '',
      tableNumber: 1,
    },
    paymentRecords: {
      totalAmount: 0,
      payments: [],
    },
  };
  const [data, setData] = useState(posDataDefault);

  useEffect(() => {
    setData(posDataDefault);
  }, [posAccess.drawerId]);

  // Reset POS entirely when the user switches location
  useEffect(() => {
    setCurrentTab('delivery_type');
    setData(posDataDefault);
  }, [selectedLocation?.id]);

  useEffect(() => {
    setCurrentTab('delivery_type');
    setData(posDataDefault);
    if (isDrawer) {
      if (viewPosStatus) {
        dispatch(getPaymentTypes({ includeLocations: true }));
        dispatch(getAllSaleTypes());
        dispatch(getFoodCategorys('menu'));
        dispatch(getMenuAndVariationFoods());
        dispatch(updateUserDataAfterUpdate());
      }
    } else {
      dispatch(getPaymentTypes({ includeLocations: true }));
      dispatch(getAllSaleTypes());
      dispatch(getFoodCategorys('menu'));
      dispatch(getMenuAndVariationFoods());
    }
  }, [viewPosStatus]);

  useEffect(() => {
    if (drawerOpeningStatus) {
      dispatch(updateUserDataAfterUpdate());
    }
  }, [drawerOpeningStatus]);

  const onClickSound = () => {};

  const isUberDeliveryType = (deliveryInfo) => {
    const deliveryIdentifiers = [deliveryInfo?.name, deliveryInfo?.code, deliveryInfo?.description].filter(Boolean).map((value) => value.toString().toLowerCase());

    return deliveryIdentifiers.some((value) => value.includes('uber'));
  };

  const getMappedCustomerInfo = (customer) => {
    const birthday = customer?.birthday ? moment(customer?.birthday) : null;
    const hasValidBirthday = birthday?.isValid?.() || false;

    return {
      id: customer?.id || 0,
      name: customer?.name || '',
      phone: customer?.phone || '94',
      loyaltyBalance: Number(customer?.totalAmount || 0),
      birthdayMonth: hasValidBirthday ? Number(birthday.format('MM')) : 0,
      birthdayDay: hasValidBirthday ? Number(birthday.format('DD')) : 0,
      sendSMS: customer?.sendSms ?? customer?.sendSMS ?? true,
      isEdit: false,
      isDefaultPosUberCustomer: customer?.isDefaultPosUberCustomer === 1,
    };
  };

  const moveToNextPosStep = async (tab) => {
    if (tab !== 'customer_info' || !isUberDeliveryType(data?.deliveryInfo)) {
      setCurrentTab(tab);
      return;
    }

    try {
      const response = await get(`defaultPosUberCustomer`);
      const defaultUberCustomer = response?.data?.[0];

      if (response?.status && defaultUberCustomer?.id) {
        setData((currentData) => ({
          ...currentData,
          customerInfo: getMappedCustomerInfo(defaultUberCustomer),
        }));
        setCurrentTab('payments');
        return;
      }

      toast.warning('Default Uber customer not found. Please select the customer manually.');
      setCurrentTab('customer_info');
    } catch (error) {
      toast.warning('Unable to load the default Uber customer. Please select the customer manually.');
      setCurrentTab('customer_info');
    }
  };

  const isEligibleToGetOrder = async (tab) => {
    const response = await post(
      `eligibleForOrder`,
      {
        locationId: selectedLocation?.id,
        cart: data?.orderInfo?.cart?.map((value) => ({ saleTypeId: data?.deliveryInfo?.id, foodId: value?.id, qty: value?.quantity })),
      },
      BASE_URL,
      true, // usePromiseToast
      true // stopAutoClose
    );
    if (response?.status) {
      await moveToNextPosStep(tab);
    } else {
      if (Number(prompt('Please enter the otp to continue!')) === response?.otp) {
        await moveToNextPosStep(tab);
      } else {
        toast.error('Incorrect OTP!');
      }
    }
  };
  const POS_TABS = [
    {
      value: 'delivery_type',
      icon: <Iconify icon={'carbon:delivery'} width={20} height={20} />,
      component: <DeliveryType data={data?.deliveryInfo} setData={setData} onClickSound={onClickSound} onChangeTab={setCurrentTab} />,
      disabled: ['delivery_type'].includes(currentTab),
    },
    {
      value: 'order_info',
      icon: <Iconify icon={'material-symbols:order-approve-outline'} width={20} height={20} />,
      component: <OrderInformation data={data} setData={setData} onClickSound={onClickSound} onChangeTab={isEligibleToGetOrder} />,
      disabled: ['delivery_type', 'order_info'].includes(currentTab),
    },
    {
      value: 'customer_info',
      icon: <Iconify icon={'carbon:customer'} width={20} height={20} />,
      component: <CustomerMobileNumber data={data?.customerInfo} setData={setData} onClickSound={onClickSound} onChangeTab={setCurrentTab} />,
      disabled: ['delivery_type', 'order_info', 'customer_info'].includes(currentTab),
    },
    {
      value: 'payments',
      icon: <Iconify icon={'material-symbols:order-approve-outline'} width={20} height={20} />,
      component: <Payments data={data} setData={setData} onClickSound={onClickSound} onChangeTab={setCurrentTab} />,
      disabled: ['delivery_type', 'customer_info', 'order_info', 'payments'].includes(currentTab),
    },
  ];

  return (
    <Page title='POS'>
      {posAccess.canAccess ? (
        <Container maxWidth={themeStretch ? false : 'lg'}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Tabs allowScrollButtonsMobile variant='scrollable' scrollButtons='auto' value={currentTab} onChange={(event, newValue) => setCurrentTab(newValue)}>
                {POS_TABS.map((tab) => (
                  <Tab disableRipple key={tab.value} label={capitalCase(tab.value)} icon={tab.icon} value={tab.value} disabled={tab.disabled} />
                ))}
              </Tabs>
              {sessionIdentifiers && <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>{sessionIdentifiers}</Typography>}
            </Box>
            <Box sx={{ fontSize: 30, ml: 2, cursor: 'pointer' }}>
              <a
                onClick={() => {
                  dispatch(hidePOSTab());
                }}
              >
                x
              </a>
            </Box>
          </Box>
          <Box sx={{ mb: 5 }} />
          {POS_TABS.map((tab) => {
            return tab.value === currentTab && <Box key={tab.value}>{tab.component}</Box>;
          })}
        </Container>
      ) : (
        <Container
          maxWidth={themeStretch ? false : 'lg'}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '70vh',
          }}
        >
          <Typography variant='h5' align='center' sx={{ mb: 3 }}>
            POS Drawer Opening
          </Typography>
          <TextField
            disabled
            value={startingAmount}
            type='number'
            label={`POS Open Value`}
            sx={{ width: '35%', mb: 2 }}
            placeholder={`Amount in LKR`}
            onChange={(event) => {
              onClickSound();
              setStartingAmount(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onClickSound();
                dispatch(
                  openPOSDrawer({
                    locationId: selectedLocation?.id,
                    startingAmount: startingAmount,
                  })
                );
              }
            }}
          />
          <LoadingButton
            onClick={() => {
              onClickSound();
              dispatch(
                openPOSDrawer({
                  locationId: selectedLocation?.id,
                  startingAmount: startingAmount,
                })
              );
            }}
            variant='contained'
            size='large'
            color={'error'}
            sx={{ width: '35%' }}
          >
            Open POS Drawer
          </LoadingButton>
        </Container>
      )}
    </Page>
  );
}
